import type { EstimateOption } from "../api/pricing";
import type {
  RecipientBookEntry,
  RecipientDestination,
} from "../api/recipients";
import { PayoutMethodType, type PayoutInput } from "../interfaces/payout";

/**
 * Recipient-book destination replay helpers for flow-send.
 * Maps book destinations into `PayoutInput` and validates against live
 * pre-quote options (never re-pin `lastCapabilityKey` raw).
 */

/** Strips a country prefix from a book `stateCode` when present (`MX-JAL` → `JAL`). */
export const bareStateCode = (
  stateCode: string | undefined | null
): string | null => {
  if (!stateCode?.trim()) return null;
  const trimmed = stateCode.trim().toUpperCase();
  const dash = trimmed.indexOf("-");
  if (dash > 0 && dash <= 3) return trimmed.slice(dash + 1) || null;
  return trimmed;
};

export const destinationsForIntent = (
  destinations: RecipientDestination[],
  intent: PayoutMethodType | null
): RecipientDestination[] => {
  if (!intent) return [];
  return destinations.filter((d) => d.payoutMethod === intent);
};

const capabilityDestinationCountry = (
  capabilityKey: string | undefined
): string | null => {
  const country = capabilityKey?.split(":")[1]?.trim().toUpperCase();
  return country && /^[A-Z]{2}$/.test(country) ? country : null;
};

/** Keeps recipient-book rows scoped to the currently composed country. */
export const recipientBookEntryMatchesCountry = (
  entry: RecipientBookEntry,
  destinationCountry: string
): boolean => {
  const country = destinationCountry.trim().toUpperCase();
  if (!country) return false;

  const addressCountry = entry.address?.countryISO2?.trim().toUpperCase();
  if (addressCountry) return addressCountry === country;

  // Older operations can omit recipient.address. Their frozen capability keys
  // still prove the destination country used to build the fingerprint.
  const destinationCountries = new Set(
    entry.destinations
      .map((destination) =>
        capabilityDestinationCountry(destination.lastCapabilityKey)
      )
      .filter((value): value is string => Boolean(value))
  );
  return destinationCountries.size === 1 && destinationCountries.has(country);
};

/**
 * Maps a book destination into `PayoutInput` for the given intent.
 * Returns `null` when required fields for replay are missing.
 */
export const mapDestinationToPayoutInput = (
  destination: RecipientDestination,
  intent: PayoutMethodType
): PayoutInput | null => {
  if (destination.payoutMethod !== intent) return null;

  switch (intent) {
    case PayoutMethodType.CASH_PICKUP: {
      // Require state + payer. Payer-only habits (ANY SKU without
      // recipient.address.state) are not offered — the form always asks for
      // a subdivision, so one-tap must restore both.
      const stateCode = bareStateCode(destination.cash?.stateCode);
      const payerId = destination.cash?.payerCode?.trim() || null;
      if (!payerId || !stateCode) return null;
      return {
        source: "saved",
        payerId,
        stateCode,
      };
    }
    case PayoutMethodType.BANK_ACCOUNT: {
      if (!destination.paymentMethodId) return null;
      const last4 =
        destination.display?.maskedAccount?.replace(/\D/g, "").slice(-4) ||
        undefined;
      return {
        source: "saved",
        paymentMethodId: destination.paymentMethodId,
        bankId: destination.display?.bankName ?? undefined,
        last4,
      };
    }
    case PayoutMethodType.PUSH_TO_CARD: {
      if (!destination.paymentMethodId) return null;
      return {
        source: "saved",
        paymentMethodId: destination.paymentMethodId,
        brand: destination.display?.cardBrand ?? "unknown",
        last4: destination.display?.cardLast4 ?? "0000",
      };
    }
    case PayoutMethodType.MOBILE_WALLET: {
      const phone = destination.phone?.trim();
      if (!phone) return null;
      return { source: "saved", phone, carrierId: undefined };
    }
    default:
      return null;
  }
};

const payerCodeOf = (payout: PayoutInput, method: PayoutMethodType): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

/**
 * Resolves the live SKU for a payout selection from pre-quote options.
 */
export const resolveCapabilityKeyForPayout = (
  method: PayoutMethodType,
  payout: PayoutInput,
  options: EstimateOption[]
): string | null => {
  const forMethod = options.filter((o) => o.payoutMethod === method);
  if (forMethod.length === 0) return null;

  const payerCode = payerCodeOf(payout, method);
  if (payerCode) {
    const byPayer = forMethod.find((o) => o.payerCode === payerCode);
    if (byPayer) return byPayer.capabilityKey;
    if (
      method === PayoutMethodType.CASH_PICKUP ||
      method === PayoutMethodType.MOBILE_WALLET
    ) {
      return null;
    }
  }

  if (
    (method === PayoutMethodType.BANK_ACCOUNT ||
      method === PayoutMethodType.PUSH_TO_CARD) &&
    (payout.source === "saved" || payout.paymentMethodId)
  ) {
    return forMethod[0]?.capabilityKey ?? null;
  }

  return forMethod[0]?.capabilityKey ?? null;
};

export interface CompatibleBookDestination {
  destination: RecipientDestination;
  payout: PayoutInput;
  capabilityKey: string;
}

export const compatibleBookDestinations = (
  destinations: RecipientDestination[],
  intent: PayoutMethodType | null,
  quoteOptions: EstimateOption[]
): CompatibleBookDestination[] => {
  if (!intent) return [];
  const out: CompatibleBookDestination[] = [];
  for (const destination of destinationsForIntent(destinations, intent)) {
    const payout = mapDestinationToPayoutInput(destination, intent);
    if (!payout) continue;
    const liveKey = resolveCapabilityKeyForPayout(
      intent,
      payout,
      quoteOptions
    );
    // While pre-quote options are empty (still loading), keep habits visible
    // via lastCapabilityKey. Once options resolve, require a live match so we
    // never one-tap a payer the engine can no longer quote.
    const capabilityKey =
      liveKey ??
      (quoteOptions.length === 0
        ? destination.lastCapabilityKey ?? null
        : null);
    if (!capabilityKey) continue;
    out.push({ destination, payout, capabilityKey });
  }
  return out;
};

export const destinationListKey = (destination: RecipientDestination): string => {
  if (destination.paymentMethodId) return `pm:${destination.paymentMethodId}`;
  if (destination.cash) {
    return `cash:${destination.payoutMethod}:${destination.cash.stateCode ?? ""}:${destination.cash.payerCode ?? ""}`;
  }
  if (destination.phone) return `wallet:${destination.phone}`;
  return `method:${destination.payoutMethod}:${destination.lastUsedAt}`;
};

/**
 * When the recipient changes, drop a payout selection only if it pins a saved
 * instrument (`paymentMethodId`) that is not in the new recipient's book.
 * Cash pickup / wallet / newly typed PAN-CLABE have no pmId and stay armed.
 */
export const shouldClearPayoutOnRecipientChange = (
  payout: PayoutInput | null | undefined,
  destinations: RecipientDestination[]
): boolean => {
  const pmId = payout?.paymentMethodId;
  if (!pmId) return false;
  return !destinations.some((d) => d.paymentMethodId === pmId);
};
